import './App.css';

import { Suspense, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Routes, useLocation } from 'react-router-dom';
import { Bounce, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useUserContext } from './context/userContext';

import { persistor } from './redux/store';
import { reportWebviewPerf, markWebviewMilestone, trackWebviewMilestone } from './utils/postMessage';

// Route groups
import productRoutes from './routes/product';
import postCreationRoutes from './routes/post-creation';
import supportRoutes from './routes/support';
import surveyRoutes from './routes/survey';
import gamificationRoutes from './routes/gamification';
import paymentRoutes from './routes/payment';
import creatorRoutes from './routes/creator';
import contentRoutes from './routes/content';
import miscRoutes from './routes/misc';
import Loader from './components/kb-experiment/widgets/Loader';
import BridgeNavigation from './components/bridge-navigation';

const excludedEndpoints = [
	'/nonretailer/verification',
	'/nonretailer/verification/',
	'/nonretailer/',
	'/nonretailer',
];

function App() {
	const dispatch = useDispatch();
	const { setUserDetails } = useUserContext();
	const search = useLocation().search;
	const userId = new URLSearchParams(search).get('userId');

	useEffect(() => {
		const pathname = window?.location?.pathname;
		if (!excludedEndpoints.includes(pathname)) {
			const data = userId ?? window.sessionStorage.getItem('data');
			const webengageLicense = process.env.REACT_APP_WEBENGAGE_LICENSE;
			// Defer mixpanel-browser off the boot bundle: identify is analytics
			// linkage, not user-visible, so a beat late is fine. The kb-experiment
			// screens track via the RN bridge (native SDK), so the seller route
			// never needs mixpanel-browser synchronously.
			import('./utils/mixpanel').then(({ default: mixpanel }) => mixpanel.identify(data));

			// Only initialize WebEngage if license is available and webengage exists
			if (webengageLicense && window.webengage) {
				try {
					window.webengage.init(webengageLicense);
					window.webengage.user.login(data);
				} catch (error) {
					/* error handled silently */
				}
			}
		}
	}, [dispatch, setUserDetails, userId]);

	useEffect(() => {
		persistor.purge();
	}, [dispatch]);

	// One perf beacon per document load, after the React shell has mounted —
	// feeds the native 'WebView Loaded' event. Must stay first-render-only.
	useEffect(() => {
		reportWebviewPerf();
		// Page-side twin of the beacon above. reportWebviewPerf hands its numbers to
		// native to fold into 'WebView Loaded' — an event that fires 0 times in prod —
		// so the React-mounted milestone was invisible. This one goes over mp-track and
		// lands regardless.
		markWebviewMilestone('mounted');
		trackWebviewMilestone('Webview App Mounted');
	}, []);

	return (
		<>
			{/* Native-bridge routing for preloaded/parked WebView instances
			    (kc-navigate client-side nav + refetch-on-show). Inert outside the
			    app WebView. */}
			<BridgeNavigation />
			<Suspense
				fallback={
					<div
						style={{
							minHeight: '100vh',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}
					>
						<Loader size={56} />
					</div>
				}
			>
				<Routes>
					{productRoutes}
					{postCreationRoutes}
					{supportRoutes}
					{surveyRoutes}
					{gamificationRoutes}
					{paymentRoutes}
					{creatorRoutes}
					{contentRoutes}
					{miscRoutes}
				</Routes>
			</Suspense>
			<ToastContainer
				position="top-right"
				autoClose={3000}
				newestOnTop={false}
				closeOnClick
				rtl={false}
				pauseOnFocusLoss
				theme="light"
				// React 19 ignores function-component defaultProps, and react-toastify@9
				// supplies its transition ONLY via ToastContainer.defaultProps. Without
				// this explicit prop every toast renders createElement(undefined) →
				// "Element type is invalid" → the app-level ErrorBoundary fallback
				// (कुछ गड़बड़ हो गई) instead of the toast (Sentry WEBAPPS-REACT-62).
				transition={Bounce}
			/>
		</>
	);
}

export default App;
